import { TestId } from '@repo/test-id';
import { useNavigate } from 'react-router-dom';
import { tr } from '@repo/locale';
import { useState } from 'react';
import { useVehicleActions } from '@repo/domain-service';

import Header from '@features/reservation/components/Header';
import VehicleFieldGroup from '@features/reservation/components/vehicle/VehicleFieldGroup';
import ProcessProgress from '@features/reservation/components/process/ProcessProgress';
import { selectStatus, useAuthStore } from '@features/auth';

import PageFrame from '@shared/components/page/PageFrame';
import { handleRepositoryError } from '@shared/utils/handleRepositoryError';

interface Props {
  secondsToDeadline?: number;
  onExpire?: () => void;
}

export default function CreateVehicleContents({ secondsToDeadline, onExpire }: Props) {
  const navigate = useNavigate();
  const testId = TestId.root('reservation.vehiclesNoneExist');
  const authStatus = useAuthStore(selectStatus);
  const showFieldName = authStatus === 'authenticated';

  const { getOrCreateVehicle, savingVehicles } = useVehicleActions();

  const [registrationNumber, setRegistrationNumber] = useState<string>('');
  const [vehicleName, setVehicleName] = useState<string>('');

  const handleBack = () => {
    navigate(-1);
  };

  const handleFormSubmit = async () => {
    try {
      await getOrCreateVehicle({ registrationNumber, name: vehicleName });
    } catch (error) {
      handleRepositoryError(error, {
        fallback: tr('reservations:vehicle_creation.error'),
        testId,
      });
    }
  };

  return (
    <PageFrame
      testId={testId}
      onGoBack={handleBack}
      forwardButton={{
        label: tr('reservations:no_vehicle_exists_page.add_button'),
        disabled: !registrationNumber.trim() || savingVehicles,
        onClick: handleFormSubmit,
      }}
    >
      <div className="flex flex-col gap-6">
        <ProcessProgress
          currentStep={2}
          secondsToDeadline={secondsToDeadline}
          onExpire={onExpire}
          testId={testId.append('progress')}
        />

        <Header
          title={tr('reservations:no_vehicle_exists_page.title')}
          subtitle={tr('reservations:no_vehicle_exists_page.subtitle')}
          testId={testId.append('title')}
        />
      </div>

      <div className="mt-4">
        <div className="bg-primary flex flex-col gap-6 rounded-2xl p-4 shadow-xs">
          <VehicleFieldGroup
            showNameField={showFieldName}
            registrationNumber={registrationNumber}
            onRegistrationNumberChange={setRegistrationNumber}
            vehicleName={vehicleName}
            onVehicleNameChange={setVehicleName}
            testId={testId.append('vehicle')}
          />
        </div>
      </div>
    </PageFrame>
  );
}
